import { apiFetch } from '@/api/client';
import type { Invoice } from '@/types/invoice';
import type { InvoiceStatus } from '@/types/invoice-status';
import type { NotaryStatus } from '@/types/notary-status';

const BASE_URL = '/open/v1/invoices';

export const invoiceKeys = {
  all: ['invoices'] as const,
  detail: (id: string) => ['invoices', id] as const,
};

export interface InvoiceListCompetencyPeriod {
  from: string;
  to: string;
}

export interface InvoiceListItem {
  invoiceId: string;
  storeId: string;
  storeTradeName: string;
  title: string;
  competencyPeriods: InvoiceListCompetencyPeriod[];
  dueDate: string;
  amount: number;
  status: InvoiceStatus;
  notaryStatus?: NotaryStatus;
  isNegotiation: boolean;
}

export interface InvoiceListResponse {
  content: InvoiceListItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

interface GetInvoicesParams {
  page: number;
  size: number;
  dueDate?: string;
  status?: InvoiceStatus;
  storeId?: string;
  isNegotiation?: boolean;
}

export const getInvoices = ({
  page,
  size,
  dueDate,
  status,
  storeId,
  isNegotiation,
}: GetInvoicesParams): Promise<InvoiceListResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });

  if (dueDate) {
    params.set('dueDate', dueDate);
  }

  if (status) {
    params.set('status', status);
  }

  if (storeId) {
    params.set('storeId', storeId);
  }

  if (isNegotiation !== undefined) {
    params.set('isNegotiation', String(isNegotiation));
  }

  return apiFetch(`${BASE_URL}?${params.toString()}`);
};

export const getInvoice = (id: string): Promise<Invoice> => apiFetch(`${BASE_URL}/${id}`);

export interface GeneratePaymentRequestsResult {
  invoiceId: string;
  createdMethods: string[];
  boletoPaymentRequestId?: string;
  pixPaymentRequestId?: string;
  error?: string;
}

export interface GeneratePaymentRequestsResponse {
  results: GeneratePaymentRequestsResult[];
}

export const generatePaymentRequests = (invoiceId: string): Promise<GeneratePaymentRequestsResponse> =>
  apiFetch(`${BASE_URL}/generate-payment-requests`, {
    method: 'POST',
    body: JSON.stringify({ invoiceIds: [invoiceId] }),
  });
